import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { setPageProperties } from 'shared-utils/analytics';
import { TaskContext } from '../components/tasks/contexts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { client, OutboundTaskTypeKey } from '../api';
import type { TaskResponse, RawTaskResponse } from '../components/tasks/types';
import { validateTaskResponse, STRUCTURED_TASK_TYPES_GENERIC_TASK_VIEW } from '../components/tasks/types';
import { taskQueryKey, tasksQueryKey } from '../queries';
import TaskView from '../components/tasks/TaskView';
import UnstructuredTaskView from '../components/tasks/UnstructuredTaskView';
import PaInformationRequestTaskView from '../components/tasks/taskViews/PaInformationRequestTaskView';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import PageContainer from '../components/PageContainer';
import {
  DocumentUploadTaskView,
  UnableToVerifyInsuranceTaskView,
  PaDenialNextStepsTaskView,
  PaDenialNextStepsDynamicTaskView,
  PaExpiringTaskView,
  AppealReviewTaskView,
  PaDenialNextStepsAndAppealReviewTaskView,
} from '../components/tasks/taskViews';
import LetterOfMedicalNecessityTaskView from '../components/tasks/LetterOfMedicalNecessityTaskView';
import TremfyaExpressEnrollmentTaskView from '../components/tasks/taskViews/TremfyaExpressEnrollmentTaskView';
import FormSignatureTaskView from '../components/tasks/taskViews/FormSignatureTaskView';

const TaskPage: React.FC = () => {
  // TODO: use taskQuery.isPending instead
  const [updating, setUpdating] = useState(false);
  const params = useParams();
  const taskId = params.taskId!;

  const queryClient = useQueryClient();
  const taskQuery = useQuery<TaskResponse>({
    queryKey: taskQueryKey(taskId),
    queryFn: async () => {
      const res = await client.get<RawTaskResponse>(`tasks/${taskId}`);
      return validateTaskResponse(res.data);
    },
  });

  useEffect(() => {
    if (taskQuery.data) {
      setPageProperties({
        task_type: taskQuery.data.task.type,
        task_key: taskQuery.data.task.task_key,
        task_id: taskQuery.data.task.id,
        task_status: taskQuery.data.task.status,
      });
    }
  }, [taskQuery.data]);

  let body;
  if (taskQuery.isLoading) {
    body = <LoadingSpinner />;
  } else if (taskQuery.error) {
    body = <PageLoadError />;
  } else if (taskQuery.data) {
    body = (
      <TaskContext.Provider
        value={{
          taskId,
          updating,
          setUpdating,
          rxId: taskQuery.data.drug?.id ?? null,
          onTaskUpdate: async () => {
            queryClient.invalidateQueries({
              queryKey: tasksQueryKey(),
            });
            queryClient.invalidateQueries({
              queryKey: taskQueryKey(taskId),
            });
            setUpdating(false);
          },
        }}
      >
        <InnerTaskPage taskResponse={taskQuery.data} />
      </TaskContext.Provider>
    );
  }

  return <PageContainer noPadding={true}>{body}</PageContainer>;
};

interface InnerTaskPageProps {
  taskResponse: TaskResponse;
}

const InnerTaskPage: React.FC<InnerTaskPageProps> = ({ taskResponse }) => {
  switch (taskResponse.task_response_kind) {
    case OutboundTaskTypeKey.NEW_RX_DOCUMENT_UPLOAD: {
      return <DocumentUploadTaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
    }
    case OutboundTaskTypeKey.LETTER_OF_MEDICAL_NECESSITY:
      return <LetterOfMedicalNecessityTaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
    case OutboundTaskTypeKey.DIGITAL_ENROLLMENT_TREMFYA:
      return <TremfyaExpressEnrollmentTaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
    case OutboundTaskTypeKey.PA_INFORMATION_REQUEST:
    case OutboundTaskTypeKey.PA_INFORMATION_REQUEST_UNMET_CRITERIA:
      return <PaInformationRequestTaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
    case OutboundTaskTypeKey.UNABLE_TO_VERIFY_INSURANCE:
      return <UnableToVerifyInsuranceTaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
    case OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS:
      return <PaDenialNextStepsTaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
    case OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS_DYNAMIC:
      return <PaDenialNextStepsDynamicTaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
    case OutboundTaskTypeKey.PA_EXPIRING:
      return <PaExpiringTaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
    case OutboundTaskTypeKey.APPEAL_REVIEW:
      return <AppealReviewTaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
    case OutboundTaskTypeKey.PA_DENIAL_NEXT_STEPS_AND_APPEAL_REVIEW:
      return <PaDenialNextStepsAndAppealReviewTaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
    default: {
      if (taskResponse.task.task_key === OutboundTaskTypeKey.FORM_SIGNATURE) {
        if (taskResponse.task.form) {
          return <FormSignatureTaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
        }
        return <UnstructuredTaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
      }
      // for tasks that are always structured, return the generic structured view component
      if (STRUCTURED_TASK_TYPES_GENERIC_TASK_VIEW.includes(taskResponse.task.task_key)) {
        return <TaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
      }
      return <UnstructuredTaskView key={taskResponse.task.id} taskResponse={taskResponse} />;
    }
  }
};

export default TaskPage;
